import { useCallback, useRef, useState } from 'react';

import cn from 'classnames';
import Webcam from 'react-webcam';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { ValidationAlert } from 'components/FormComponents';

import styles from './Selfie.module.scss';

const Screens = {
  Intro: 'intro',
  Camera: 'camera',
  Confirm: 'confirm',
};

const Selfie = ({ brand, onSave, screenName = 'intro', noBackButton = false }) => {
  const webcamRef = useRef(null);
  const [screen, setScreen] = useState(screenName || Screens.Intro);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /** Capture photo */
  const handleCapture = useCallback(() => {
    try {
      const shot = webcamRef.current?.getScreenshot();
      if (!shot) {
        setError('Unable to capture image. Please try again.');
        return;
      }
      setPhoto(shot);
      setError(null);
      setScreen(Screens.Confirm);
    } catch (err) {
      console.error('[Selfie] Capture error:', err);
      setError('An error occurred while capturing the image.');
    }
  }, []);

  /** Save photo */
  const handleSave = useCallback(async () => {
    if (!photo || saving) return;

    setSaving(true);
    setError(null);
    try {
      await onSave(photo);
    } catch (err) {
      console.error('[Selfie] Save error:', err);
      setError('Failed to save selfie. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [photo, saving, onSave]);

  /** Retake */
  const handleRetake = useCallback(() => {
    setPhoto(null);
    setError(null);
    setScreen(Screens.Camera);
  }, []);

  const handleUserMediaError = useCallback(err => {
    const errorMessage =
      err?.name === 'NotAllowedError'
        ? 'Access to the camera is denied. Please check your browser settings and reload the page to try again.'
        : 'An error occurred while accessing the camera. Please try again.';
    setError(errorMessage);
  }, []);

  /** Render screens */
  const renderScreen = () => {
    switch (screen) {
      case Screens.Intro:
        return (
          <div className="d-flex flex-column u-gap-medium">
            <img
              src="assets/images/illustration-selfie.svg"
              className={styles.illustration}
              alt="Selfie illustration"
            />
            <span className="primary-text u-font-weight-500 u-font-size-x-large">
              Identity Liveness Check
            </span>
            <div className="d-flex flex-column u-gap">
              <p className="secondary-text u-font-size-small u-margin-0">
                To complete your verification, you’ll be asked to take a selfie using your device’s
                camera.
              </p>
              <p className="secondary-text u-font-size-small u-margin-0">
                This selfie will be securely compared with your uploaded ID to confirm your identity
                and ensure you’re physically present.
              </p>
            </div>
            <CustomButton brand={brand} small onClick={() => setScreen(Screens.Camera)}>
              Take Selfie
            </CustomButton>
          </div>
        );

      case Screens.Camera:
        return (
          <div className="d-flex flex-column u-gap-large">
            {error ? (
              <div className="d-flex align-items-center justify-content-center">
                <img
                  src="assets/images/camera-issue.jpg"
                  alt="Application submitted illustration"
                  className="u-width-75 u-border-radius--half"
                />
              </div>
            ) : (
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                className={cn('u-border u-border-radius--xxx-large')}
                videoConstraints={{ facingMode: 'user' }}
                onUserMediaError={err => handleUserMediaError(err)}
                style={{ objectFit: 'cover' }}
                height={500}
                mirrored
                playsInline
              />
            )}

            {error && <ValidationAlert show={true} message={error} />}

            {!error && (
              <span className="secondary-text u-font-size-small u-margin-0 u-text-centered">
                Hold your face up to the camera viewport
              </span>
            )}

            <div className="d-flex align-items-center justify-content-between u-gap-large">
              {!noBackButton && (
                <CustomButton type="secondary" onClick={() => setScreen(Screens.Intro)}>
                  Back
                </CustomButton>
              )}
              <CustomButton brand={brand} onClick={handleCapture}>
                Take Selfie
              </CustomButton>
            </div>
          </div>
        );

      case Screens.Confirm:
        return (
          <div className="d-flex flex-column u-gap-large">
            <img
              src={photo}
              className="u-border u-border-radius--xxx-large"
              alt="Captured selfie"
            />
            {error && <p className="u-text-danger u-margin-top">{error}</p>}
            <div className="d-flex align-items-center justify-content-between u-gap-large">
              <CustomButton
                type="secondary"
                className={cn('u-margin-right', 'u-margin-top--0')}
                onClick={handleRetake}>
                Retake
              </CustomButton>
              <CustomButton
                brand={brand}
                className="u-margin-top--0"
                onClick={handleSave}
                disabled={saving}>
                {saving ? 'Saving…' : 'Confirm Selfie'}
              </CustomButton>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return <div className={styles.selfieWrapper}>{renderScreen()}</div>;
};

export default Selfie;
